import React from 'react';
import {PropTypes} from 'prop-types';
import moment from 'moment-timezone';
import numeral from 'numeral';
import {ErrorsBlockComponent} from '../../shared/errorsBlockComponent';
import {ButtonBusyIndicatorComponent} from '../../shared/buttonBusyIndicatorComponent';
import {Link} from '../../shared/link';
import {Pagination} from '../../shared/Pagination';
import {PageHeader} from '../../shared/PageHeader';
import {MakeApiCall, WriteConsoleErrors, ToString, ObjectToQueryString, ReplaceUrl, IsVersionFilterValid} from '../../shared/utils';
import {FlagComponent} from '../AccountsListComponent/flag';
import {AccountComponent} from '../AccountsListComponent/account';
import {SimpleFilterItemComponent} from '../SimpleFilterItem';
import {UrlBuilder, APPLICATION_TYPE} from '../../app.defs';

import './styles.scss';

const AGENTS_ACTION = {
    NONE: 0,
    LOAD: 1
}

export class AgentsListComponent extends React.Component {
    constructor(props, ...others) {
        super(props, ...others);
        this.state = {
            isLoaded: false,
            agentFilter: props.agentFilter,
            versionFilter: props.versionFilter,
            jobFilter: props.jobFilter,
            deletedStateId: props.deletedStateId,
            applicationTypeId: props.applicationTypeId,
            pageNum: props.pageNum,
            pageSize: props.pageSize,
            action: AGENTS_ACTION.NONE,
            totalCount: 0,
            errors: null,
            agents: [],
            requestId: 0
        }

        this.doLoad = this.doLoad.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
        this.renderForm = this.renderForm.bind(this);
        this.renderTable = this.renderTable.bind(this);
        this.renderPagination = this.renderPagination.bind(this);
    }

    componentDidMount() {
        const self = this;

        self._isMounted = true;
        self.doLoad();
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    doLoad() {
        const self = this;
        if (!self._isMounted) {
            return;
        }

        const requestId = self.state.requestId + 1;

        const data = {
            agtf: self.state.agentFilter,
            agvf: self.state.versionFilter,
            jobf: self.state.jobFilter,
            apt: self.state.applicationTypeId,
            ds: self.state.deletedStateId,
            pn: self.state.pageNum,
            ps: self.state.pageSize
        }

        new Promise(resolve => {
            self.setState({action: AGENTS_ACTION.LOAD, errors: null, requestId: requestId}, resolve);
        })
        .then(() => {
            if (self._isMounted) {
                ReplaceUrl({
                    pathname: window.location.pathname,
                    search: ObjectToQueryString(data, true, {pn:1, ps: 50})
                });

                return MakeApiCall(
                    $.get(
                        {
                            url: '/api/admin/getAgents',
                            data: data,
                            dataType: 'json',
                            cache: false
                        }
                    ),
                    false,
                    () => !self._isMounted || (self.state.requestId != requestId)
                );
            }
        })
        .then(response => {
            if (self._isMounted && (self.state.requestId == requestId)) {
                self.setState({
                    action: AGENTS_ACTION.NONE,
                    isLoaded: true,
                    agentFilter: response.agtf,
                    versionFilter: response.agvf,
                    jobFilter: response.jobf,
                    applicationTypeId: response.apt,
                    deletedStateId: response.ds,
                    pageNum: response.pn,
                    pageSize: response.ps,
                    agents: response.Agents,
                    totalCount: response.TotalCount
                });
            }
        })
        .catch(errors => {
            WriteConsoleErrors(errors, true, false);
            if (self._isMounted && (self.state.requestId == requestId)) {
                self.setState({action: AGENTS_ACTION.NONE, errors: errors})
            }
        });
    }

    handleSubmit(event) {
        event.preventDefault();
        this.doLoad();
    }

    renderForm() {
        const self = this;
        const {action, agentFilter, versionFilter: _versionFilter, jobFilter, deletedStateId, applicationTypeId} = self.state;
        const isDisabled = action != AGENTS_ACTION.NONE;
        const versionFilter = ToString(_versionFilter);
        const isVersionFilterValid = IsVersionFilterValid(versionFilter.trim());

        const _renderFilterLink = (filter, negativeFilter, filterPattern) => {
            return (
                <SimpleFilterItemComponent
                    disabled={isDisabled}
                    filters={agentFilter}
                    filter={filter}
                    detectPattern={filterPattern}
                    negativeDetectPattern={negativeFilter}
                    onChange={f => self.setState({agentFilter: f})}
                />
            )
        }

        return (
            <div className="mb-n2 mt-2">
                <form className="form-inline" onSubmit={self.handleSubmit}>
                    <input 
                        type="text"
                        className="form-control mr-2 mb-2"
                        placeholder="AgentId, Name, AgentKey, :filter"
                        style={{width: '400px', maxWidth: '100%'}}
                        disabled={isDisabled}
                        value={ToString(agentFilter)}
                        onChange={e => self.setState({agentFilter: e.target.value})}
                    />
                    <input 
                        type="text"
                        className={'form-control mr-2 mb-2' + (isVersionFilterValid ? '' : ' is-invalid')}
                        placeholder="App version"
                        style={{width: '150px', maxWidth: '100%'}}
                        disabled={isDisabled}
                        value={versionFilter}
                        onChange={e => self.setState({versionFilter: e.target.value})}
                    />
                    <input 
                        type="text"
                        className="form-control mr-2 mb-2"
                        placeholder="JobId, Name"
                        style={{width: '290px', maxWidth: '100%'}}
                        disabled={isDisabled}
                        value={ToString(jobFilter)}
                        onChange={e => self.setState({jobFilter: e.target.value})}
                    />
                    <select
                        className="custom-select mr-2 mb-2"
                        disabled={isDisabled}
                        value={ToString(applicationTypeId)}
                        onChange={e => self.setState({applicationTypeId: e.target.value})}
                    >
                        {self.props.applicationTypes.map(p => <option key={p.Key} value={p.Key}>{p.Value}</option>)}
                    </select>
                    <select
                        className="custom-select mr-2 mb-2"
                        disabled={isDisabled}
                        value={ToString(deletedStateId)}
                        onChange={e => self.setState({deletedStateId: e.target.value})}
                    >
                        {self.props.deletedStates.map(p => <option key={p.Key} value={p.Key}>{p.Value}</option>)}
                    </select>
                    <button 
                        type="submit"
                        className="btn btn-outline-primary mb-2"
                        disabled={isDisabled || !isVersionFilterValid}
                    >
                        <span className="fa fa-search mr-1" />
                        Search
                        <ButtonBusyIndicatorComponent visible={action == AGENTS_ACTION.LOAD} />
                    </button>
                </form>
                <div className="small">
                    Filters:&nbsp;
                    <span className="mre-1 text-nowrap d-inline-block">{_renderFilterLink(':act', ':nact')}, {_renderFilterLink(':nact', ':act')} — active or not</span>
                    <span className="mre-1 text-nowrap d-inline-block">{_renderFilterLink(':job', ':njob')}, {_renderFilterLink(':njob', ':job')} — with at least one job or not</span>
                    <span className="mre-1 text-nowrap d-inline-block">{_renderFilterLink(':dbms', ':ndbms')}, {_renderFilterLink(':ndbms', ':dbms')} — with at least one DBMS or not</span>
                    <span className="mre-1 text-nowrap d-inline-block">{_renderFilterLink(':online', ':offline')}, {_renderFilterLink(':offline', ':online')} — status</span>
                </div>
            </div>
        )
    }

    renderTable() {
        const self = this;
        const {agents} = self.state;
        const {timeZoneId} = self.props;
        
        const tzNow = moment.utc().tz(timeZoneId);
        const tzName = tzNow.format('z');
        const existsNew = agents.some(p => tzNow.isSame(moment.utc(p.CreatedAt).tz(timeZoneId), 'day'));
        const renderStateColumn = agents.some(p => Boolean(p.DeletedAt) || !Boolean(p.IsActive));

        return (
            <table className="agents table-striped-even table-hover table-responsive newstyle">
                <thead>
                    <tr>
                        {existsNew && <th width="1%" />}
                        <th width="1%">Agent#</th>
                        <th width="1%" className="text-nowrap">Created ({tzName})</th>
                        <th width="1%">Type</th>
                        <th width="1%">Version</th>
                        <th width="35%">Name</th>
                        <th width="1%">DBMSs</th>
                        <th width="1%">Jobs</th>
                        <th width="35%">Account</th>
                        <th width="1%" className="text-center"><span className="fa fa-globe" /></th>
                        <th width="25%">Email</th>
                        {renderStateColumn && 
                            <th width="1%">State</th>
                        }
                    </tr>
                </thead>
                <tbody>
                    {agents.map(p => {
                        let rowClass = '';

                        if (Boolean(p.DeletedAt)) {
                            rowClass = 'danger';
                        }
                        else if (!Boolean(p.IsActive)) {
                            rowClass = 'secondary';
                        }
                        const operationSystemIcon = APPLICATION_TYPE.getOSIconClassName(p.ApplicationTypeId);

                        return (
                            <tr key={'agent-' + p.Id} className={rowClass}>
                                {existsNew && 
                                    <td>
                                        {tzNow.isSame(moment.utc(p.CreatedAt).tz(timeZoneId), 'day') && <span className="fa fa-flash text-success" />}
                                    </td>
                                }
                                <td>
                                    <Link to={UrlBuilder.AgentInfo({AgentId: p.Id})}>{p.Id}</Link>
                                    {p.IsOnline &&
                                        <small className="text-success ml-1">on</small>
                                    }
                                    {!p.IsOnline &&
                                        <small className="text-danger ml-1">off</small>
                                    }
                                </td>
                                <td className="text-nowrap">{moment.utc(p.CreatedAt).tz(timeZoneId).format('L LT')}</td>
                                <td className="text-nowrap"><i className={"mre-1 " + operationSystemIcon} />{p.ApplicationTypeName}</td>
                                <td className="text-nowrap">{p.Version}</td>
                                <td>{p.Name}</td>
                                <td>{p.ConnectionsCount}</td>
                                <td>{p.JobsCount}</td>
                                <td>
                                    <AccountComponent 
                                        id={p.Account.Id}
                                        name={p.Account.Name}
                                        showId
                                        showLoginAs
                                        linkToInfo
                                    />
                                </td>
                                <td> 
                                    <FlagComponent
                                        countryId={p.Account.CountryId}
                                        countryName={p.Account.CountryName}
                                        lastIP={p.Account.LastIP}
                                    />
                                </td>
                                <td>{p.Account.Email}</td>
                                {renderStateColumn &&
                                    <td className="text-nowrap">
                                        {Boolean(p.DeletedAt) && `Deleted at ${moment.utc(p.DeletedAt).tz(timeZoneId).format('L LT')}`}
                                        {!Boolean(p.DeletedAt) && !Boolean(p.IsActive) && 'disconnected'}
                                    </td>
                                }
                            </tr>
                        )
                    })}
                </tbody>
            </table>
        )    
    }

    renderPagination() {
        const self = this;
        const {pageNum, pageSize, totalCount}  = self.state;

        const pagesCount = Math.ceil(totalCount / Math.max(1, pageSize));

        if (pagesCount <= 1) {
            return null;
        }

        return (
            <Pagination
                className="justify-content-end"
                pagesCount={pagesCount}
                pageNum={pageNum}
                maxButtons={7}
                onPageSelect={(pn) => self.setState({pageNum: pn}, self.doLoad)}
                size="sm"
            />
        )
    }

    render() {
        const self = this;
        const {totalCount, isLoaded, errors} = self.state;

        return (
            <div>
                <PageHeader
                    fixed
                    captionTruncate
                    notWrappedMaxHeight={null}
                    caption={
                        <h2 className="text-truncate">Agents</h2>
                    }
                >
                    {self.renderForm()}
                </PageHeader>
                <ErrorsBlockComponent errors={errors} />
                {isLoaded && <div className="small">{`${totalCount} agent(s) found`}</div>}
                {self.renderTable()}
                <div className="pt-3">
                    {self.renderPagination()}
                </div>
           </div>
        )
    }
}
AgentsListComponent.propTypes = {
    pageNum: PropTypes.number,
    pageSize: PropTypes.number,
    timeZoneId: PropTypes.string.isRequired,
    agentFilter: PropTypes.string,
    versionFilter: PropTypes.string,
    jobFilter: PropTypes.string,
    deletedStateId: PropTypes.string,
    applicationTypeId: PropTypes.string,
    deletedStates: PropTypes.arrayOf(PropTypes.shape({
        Key: PropTypes.string.isRequired,
        Value: PropTypes.string.isRequired
    })).isRequired,
    applicationTypes: PropTypes.arrayOf(PropTypes.shape({
        Key: PropTypes.string.isRequired,
        Value: PropTypes.string.isRequired
    })).isRequired,
}
AgentsListComponent.defaultProps = {
    pageNum: 1,
    pageSize: 50,
}
